import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig } from './syncHelpers';

// Cache for user info (to avoid repeated lookups)
const userInfoCache = new Map();

// Highlights store with Supabase sync (local-first)
// No filter - RLS handles permissions (own + shared list highlights)
export const highlights$ = observable(syncedSupabase({
  supabase,
  collection: 'reedle_highlights',
  // No filter - let RLS handle (allows seeing shared list highlights)
  // Custom create/update with error handling (upsert fallback)
  create: createSyncCreate('reedle_highlights'),
  update: createSyncUpdate('reedle_highlights'),
  // Use default sync config for fieldCreatedAt, fieldUpdatedAt, changesSince
  ...defaultSyncConfig,
  // Enable realtime updates
  realtime: true,
  // Persist data locally
  persist: {
    name: 'reedle-highlights',
    plugin: ObservablePersistLocalStorage,
    retrySync: true,
  },
  // Retry configuration for offline-first
  retry: {
    infinite: true,
    backoff: 'exponential',
    maxDelay: 30000,
  },
}));

/**
 * Get user info for a highlight owner
 * Returns { username, avatar_url } from cache or fetches from Supabase
 */
export const getHighlightOwnerInfo = async (userId) => {
  if (!userId) return null;

  // Check cache first
  if (userInfoCache.has(userId)) {
    return userInfoCache.get(userId);
  }

  // Fetch from Supabase using RPC
  try {
    const { data, error } = await supabase.rpc('get_user_info', { p_user_id: userId });
    if (error || !data || data.length === 0) {
      // Fallback
      const info = { username: 'User', avatar_url: null };
      userInfoCache.set(userId, info);
      return info;
    }

    const user = data[0];
    const info = {
      username: user.raw_user_meta_data?.username || user.raw_user_meta_data?.name || user.email?.split('@')[0] || 'User',
      avatar_url: user.raw_user_meta_data?.avatar_url || null,
    };
    userInfoCache.set(userId, info);
    return info;
  } catch (err) {
    console.error('Failed to get user info:', err);
    return { username: 'User', avatar_url: null };
  }
};

/**
 * Check if a highlight belongs to current user
 */
export const isOwnHighlight = (highlight) => {
  const currentUserId = auth$.user.get()?.id;
  // Use loose comparison or string conversion to handle potential type mismatches (UUID vs string)
  return highlight?.user_id && currentUserId && String(highlight.user_id) === String(currentUserId);
};

/**
 * Get highlights for a specific article
 */
export const getHighlightsByArticle = (articleId) => {
  const highlightsObj = highlights$.get() || {};
  return Object.values(highlightsObj)
    .filter(h => h?.article_id === articleId && !h.deleted)
    .sort((a, b) => a.global_start - b.global_start);
};

/**
 * Find existing highlight at the same position (for duplicate prevention)
 * Returns the existing highlight if found, null otherwise
 */
export const findExistingHighlight = (articleId, globalStart, globalEnd) => {
  const userId = auth$.user.get()?.id;
  if (!userId) return null;

  const highlightsObj = highlights$.get() || {};
  return Object.values(highlightsObj).find(h =>
    h?.article_id === articleId &&
    h?.user_id === userId &&
    h?.global_start === globalStart &&
    h?.global_end === globalEnd &&
    !h.deleted
  ) || null;
};

/**
 * Create a new highlight (offline-capable)
 * Returns existing highlight if one already exists at the same position (duplicate prevention)
 * @param {Object} params
 * @param {string} params.articleId - Article ID
 * @param {string} params.exactText - The highlighted text
 * @param {number} params.globalStart - Start offset in rendered content
 * @param {number} params.globalEnd - End offset in rendered content
 * @param {string} [params.prefix] - Text before highlight for anchoring
 * @param {string} [params.suffix] - Text after highlight for anchoring
 * @param {number} [params.blockIndex] - Block index the highlight belongs to
 */
export const createHighlight = ({ articleId, exactText, globalStart, globalEnd, prefix, suffix, blockIndex }) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  // Check for existing highlight at the same position (duplicate prevention)
  const existing = findExistingHighlight(articleId, globalStart, globalEnd);
  if (existing) {
    console.log('Highlight already exists at this position, returning existing:', existing.id);
    return existing;
  }

  const id = uuidv4();
  const now = new Date().toISOString();
  const newHighlight = {
    id,
    article_id: articleId,
    user_id: userId,
    exact_text: exactText,
    global_start: globalStart,
    global_end: globalEnd,
    prefix: prefix || null,
    suffix: suffix || null,
    block_index: blockIndex ?? null,
    created_at: now,
    updated_at: now,
  };

  // Just set in observable - syncedSupabase handles the rest!
  highlights$[id].set(newHighlight);

  return newHighlight;
};

/**
 * Delete a highlight (offline-capable)
 */
export const deleteHighlight = (highlightId) => {
  // Just delete from observable - syncedSupabase handles sync automatically
  highlights$[highlightId].delete();
};

/**
 * Get highlight by ID
 */
export const getHighlightById = (highlightId) => {
  return highlights$[highlightId].get();
};

/**
 * Get all highlights as array
 */
export const getAllHighlights = () => {
  const highlightsObj = highlights$.get() || {};
  return Object.values(highlightsObj)
    .filter(h => h && h.id && !h.deleted)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
};

/**
 * Get total highlight count for current user
 */
export const getTotalHighlightCount = () => {
  const currentUserId = auth$.user.get()?.id;
  if (!currentUserId) return 0;

  const highlightsObj = highlights$.get() || {};
  return Object.values(highlightsObj)
    .filter(h => h && !h.deleted && String(h.user_id) === String(currentUserId))
    .length;
};

/**
 * Adjust highlight offsets after block deletion
 * @param {string} articleId - Article ID
 * @param {(offset: number) => number} adjustFn - Function that returns adjusted offset
 */
export const adjustHighlightOffsets = (articleId, adjustFn) => {
  const highlightsObj = highlights$.get() || {};

  for (const [id, highlight] of Object.entries(highlightsObj)) {
    if (highlight?.article_id === articleId && !highlight.deleted) {
      const newStart = highlight.global_start - adjustFn(highlight.global_start);
      const newEnd = highlight.global_end - adjustFn(highlight.global_end);

      highlights$[id].assign({
        global_start: newStart,
        global_end: newEnd,
        updated_at: new Date().toISOString(),
      });
    }
  }
};

